import Dinero from 'dinero.js';
import { PRICE_PER_TON_IN_CENT } from './src/constants';
import { AdditionalLineItem, LineItems, OrderAddOn } from '@libs/models';
import { DiscountTypeEnum, PricingTypeEnum } from '@libs/schemas';
import { calculateOrderAddOnAdjustmentInCents } from './order-add-ons';

function isNumberValidPercentage(num: number): boolean {
  return Number.isInteger(num) && num >= 1 && num <= 100;
}

Dinero.globalLocale = 'en-US';
Dinero.defaultPrecision = 2;
Dinero.globalFormat = '$0,0.00';

export type Money = Dinero.Dinero;

export const MoneyFromDollars = (dollars: number): Money =>
  Dinero({ amount: Math.round(Math.abs(dollars) * 100) });
export const MoneyFromCents = (cents: number): Money => Dinero({ amount: cents });

// Safe wrapper functions that handle invalid inputs gracefully
export const safeMoneyFromCents = (cents: number | null | undefined): Money => {
  // Handle null, undefined, NaN, or non-finite values
  if (cents == null || !Number.isFinite(cents)) {
    return Dinero({ amount: 0 });
  }

  // Ensure we have an integer for Dinero.js
  const safeCents = Math.round(cents);

  try {
    return Dinero({ amount: safeCents });
  } catch {
    // If Dinero still throws an error, return zero money
    // Note: Error handling should be done at the application level
    return Dinero({ amount: 0 });
  }
};

export const safeMoneyFromDollars = (dollars: number | null | undefined): Money => {
  // Handle null, undefined, NaN, or non-finite values
  if (dollars == null || !Number.isFinite(dollars)) {
    return Dinero({ amount: 0 });
  }

  try {
    return Dinero({ amount: Math.round((Math.abs(dollars) / 100) * 10000) });
  } catch {
    // If Dinero still throws an error, return zero money
    // Note: Error handling should be done at the application level
    return Dinero({ amount: 0 });
  }
};

// Export safe versions as default - backwards compatible but safer
export { safeMoneyFromCents as MoneyFromCentsSafe, safeMoneyFromDollars as MoneyFromDollarsSafe };

export class PercentBasedDiscount {
  amount: number;

  constructor(amount: number) {
    this.amount = amount;
  }

  humanReadable() {
    return `${this.amount}% off`;
  }

  getAmount() {
    return this.amount;
  }

  /**
   * Get the amount in cents. This will multiply the amount by 100.
   * @returns The amount in cents
   */
  getAmountInCents() {
    return this.amount * 100;
  }

  /**
   * Get the amount in dollars. This will divide the amount by 100.
   * @returns The amount in dollars
   */
  getAmountInDollars() {
    return this.amount / 100;
  }

  toJSON() {
    return {
      amount: this.getAmount(),
      type: this.getType(),
    };
  }

  getType(): 'percent' {
    return 'percent' as const;
  }

  isValid() {
    return isNumberValidPercentage(this.amount);
  }

  applyTo(money: Money): Money {
    return money.subtract(money.multiply(this.amount / 100));
  }

  valueFor(money: Money): Money {
    return money.subtract(this.applyTo(money));
  }
}

export class DiscountExceedsTotal extends Error {}

export class DollarBasedDiscount {
  amount: Money;

  constructor(amount: Money) {
    this.amount = amount;
  }

  toJSON() {
    return {
      amount: this.getAmount(),
      type: this.getType(),
    };
  }

  /**
   * Get the amount in cents. This will multiply the amount by 100.
   * @returns The amount in cents
   */
  getAmountInCents() {
    return this.amount.getAmount() * 100;
  }

  /**
   * Get the amount in dollars. This will divide the amount by 100.
   * @returns The amount in dollars
   */
  getAmountInDollars() {
    return this.amount.getAmount() / 100;
  }

  getAmount() {
    return this.amount.getAmount();
  }

  humanReadable() {
    return `${this.amount.toFormat()} off`;
  }

  applyTo(amount: Money): Money {
    if (!this.validFor(amount)) {
      throw new DiscountExceedsTotal();
    }
    return amount.subtract(this.amount);
  }

  isValid() {
    return true;
  }

  getType(): 'dollars' {
    return 'dollars' as const;
  }

  valueFor(amount: Money): Money {
    // Return the discount amount, but don't exceed the amount being discounted
    return this.amount.lessThanOrEqual(amount) ? this.amount : amount;
  }

  private validFor(amount: Money) {
    if (this.amount.greaterThan(amount)) {
      return false;
    }
    return true;
  }
}

export type DiscountType = PercentBasedDiscount | DollarBasedDiscount;

class InvalidDiscountData extends Error {}
export const Discount = ({ amount, type }: { amount: number; type: 'percent' | 'dollars' }) => {
  if (type === 'percent') {
    return new PercentBasedDiscount(amount);
  } else if (type === 'dollars') {
    return new DollarBasedDiscount(MoneyFromCents(amount));
  }
  throw new InvalidDiscountData();
};

class DiscountGroup {
  discounts: DiscountType[];

  constructor(discounts: DiscountType[]) {
    this.discounts = discounts;
  }

  validFor(amount: Money): boolean {
    if (this.discounts.length === 0) {
      return true;
    }

    // Calculate total discount amount using actual discount values, not capped values
    const totalDiscountAmount = this.discounts.reduce((total, discount) => {
      if (discount.getType() === 'dollars') {
        // For dollar discounts, use the actual discount amount
        return total + discount.getAmount();
      } else {
        // For percent discounts, calculate the percentage of the original amount
        return total + Math.floor(amount.getAmount() * (discount.getAmount() / 100));
      }
    }, 0);

    // Check if total discount exceeds the order amount
    return totalDiscountAmount <= amount.getAmount();
  }

  valueFor(amount: Money): Money {
    return amount.subtract(this.applyTo(amount));
  }

  applyTo(amount: Money): Money {
    if (this.discounts.length === 0) {
      return amount;
    }
    const originalAmount = amount;
    return this.discounts.reduce((total, discount) => {
      return total.subtract(discount.valueFor(originalAmount));
    }, amount);
  }
}

export const Discounts = (discounts: DiscountType[]) => new DiscountGroup(discounts);

interface ItemizedOrder {
  originalSubtotal: Money;
  originalTax: Money;
  originalTotal: Money;
  adjustedWeightTotal: Money;
  adjustedContainerTotal: Money;
  adjustedMaterialTotal: Money;
  preTaxAdditionalLineItemsTotal: Money;
  postTaxAdditionalLineItemsTotal: Money;
  addOnsAdjustment: Money; // Difference between current order add-ons and original cart add-ons
  discount: Money;
  subtotal: Money;
  totalBeforeTax: Money;
}

type ItemizedOrderInCents = {
  [_key in keyof ItemizedOrder]: number;
};

export const toItemizedOrderInCents = (itemizedOrder: ItemizedOrder): ItemizedOrderInCents => {
  return {
    originalSubtotal: itemizedOrder.originalSubtotal.getAmount(),
    originalTax: itemizedOrder.originalTax.getAmount(),
    originalTotal: itemizedOrder.originalTotal.getAmount(),
    adjustedWeightTotal: itemizedOrder.adjustedWeightTotal.getAmount(),
    adjustedContainerTotal: itemizedOrder.adjustedContainerTotal.getAmount(),
    adjustedMaterialTotal: itemizedOrder.adjustedMaterialTotal.getAmount(),
    preTaxAdditionalLineItemsTotal: itemizedOrder.preTaxAdditionalLineItemsTotal.getAmount(),
    postTaxAdditionalLineItemsTotal: itemizedOrder.postTaxAdditionalLineItemsTotal.getAmount(),
    addOnsAdjustment: itemizedOrder.addOnsAdjustment.getAmount(),
    discount: itemizedOrder.discount.getAmount(),
    subtotal: itemizedOrder.subtotal.getAmount(),
    totalBeforeTax: itemizedOrder.totalBeforeTax.getAmount(),
  };
};

export const toItemizedOrder = (itemizedOrderInCents: ItemizedOrderInCents): ItemizedOrder => {
  return {
    originalSubtotal: MoneyFromCents(itemizedOrderInCents.originalSubtotal),
    originalTax: MoneyFromCents(itemizedOrderInCents.originalTax),
    originalTotal: MoneyFromCents(itemizedOrderInCents.originalTotal),
    adjustedWeightTotal: MoneyFromCents(itemizedOrderInCents.adjustedWeightTotal),
    adjustedContainerTotal: MoneyFromCents(itemizedOrderInCents.adjustedContainerTotal),
    adjustedMaterialTotal: MoneyFromCents(itemizedOrderInCents.adjustedMaterialTotal),
    preTaxAdditionalLineItemsTotal: MoneyFromCents(
      itemizedOrderInCents.preTaxAdditionalLineItemsTotal,
    ),
    postTaxAdditionalLineItemsTotal: MoneyFromCents(
      itemizedOrderInCents.postTaxAdditionalLineItemsTotal,
    ),
    addOnsAdjustment: MoneyFromCents(itemizedOrderInCents.addOnsAdjustment),
    discount: MoneyFromCents(itemizedOrderInCents.discount),
    subtotal: MoneyFromCents(itemizedOrderInCents.subtotal),
    totalBeforeTax: MoneyFromCents(itemizedOrderInCents.totalBeforeTax),
  };
};

interface OrderLike {
  cart: {
    lineItems?: {
      totalInCents: number;
      subTotalInCents: number;
      taxInCents: number;
      addOns?: { _id: string; priceInCents?: number }[]; // Original cart add-ons (already included in subtotal)
    };
  };
  additionalLineItems?: AdditionalLineItem[];
  addOns?: OrderAddOn[]; // Current order add-ons (may differ from cart add-ons if admin modified)
  discounts?: {
    type: string;
    percentage: number;
    amountInCents: number;
    appliedRuleId?: string; // Optional for manual admin discounts
  }[];
  pricePerTonInCents?: number;
  minimumWeightInTons?: number;
}

export const itemizedOrderWithAdjustments = ({
  order,
  adjustedWeightTotal,
  adjustedContainerTotal,
  adjustedMaterialTotal,
}: {
  order: OrderLike;
  adjustedWeightTotal: Money;
  adjustedContainerTotal: Money;
  adjustedMaterialTotal: Money;
}): ItemizedOrder => {
  const originalTotal = MoneyFromCents(order.cart.lineItems!.totalInCents);
  const originalSubtotal = MoneyFromCents(order.cart.lineItems!.subTotalInCents);
  const originalTax = MoneyFromCents(order.cart.lineItems!.taxInCents);
  const [pretaxAdditionalLineItems, postTaxAdditionalLineItems] = (
    order?.additionalLineItems || []
  ).reduce(
    (acc, item) => {
      if (item.postTax) {
        acc[1].push(item);
      } else {
        acc[0].push(item);
      }
      return acc;
    },
    [[], []] as AdditionalLineItem[][],
  );
  const preTaxAdditionalLineItemsTotal =
    pretaxAdditionalLineItems?.reduce((acc, item) => {
      return acc.add(MoneyFromDollars(item.amount));
    }, MoneyFromCents(0)) || MoneyFromCents(0);
  const postTaxAdditionalLineItemsTotal =
    postTaxAdditionalLineItems?.reduce((acc, item) => {
      return acc.add(MoneyFromDollars(item.amount));
    }, MoneyFromCents(0)) || MoneyFromCents(0);

  const addOnsAdjustment = MoneyFromCents(
    calculateOrderAddOnAdjustmentInCents(order.addOns, order.cart.lineItems?.addOns),
  );

  const subtotal = originalSubtotal
    .add(adjustedWeightTotal)
    .add(adjustedContainerTotal)
    .add(preTaxAdditionalLineItemsTotal)
    .add(adjustedMaterialTotal)
    .add(addOnsAdjustment);
  const discount = Discounts(
    (order?.discounts || []).map((d) =>
      Discount({
        amount: d.percentage > 0 ? d.percentage : d.amountInCents,
        type: d.percentage > 0 ? DiscountTypeEnum.PERCENTAGE : DiscountTypeEnum.FLAT,
      }),
    ),
  ).valueFor(subtotal);
  const totalBeforeTax = subtotal.subtract(discount);
  return {
    adjustedWeightTotal,
    adjustedContainerTotal,
    adjustedMaterialTotal,
    originalSubtotal,
    originalTax,
    originalTotal,
    preTaxAdditionalLineItemsTotal,
    postTaxAdditionalLineItemsTotal,
    addOnsAdjustment,
    discount,
    subtotal,
    totalBeforeTax,
  };
};

interface OrderAdjustments {
  containerAdjustment: number;
  weightAdjustment: number;
  materialsAdjustment: number;
  containerBalanceAfterAdjustment: number;
  weightBalanceAfterAdjustment: number;
  materialsBalanceAfterAdjustment: number;
  // Enhanced refund/balance tracking
  containerRefundDue: number;
  weightRefundDue: number;
  balanceDaysDue: number;
  balanceTonsDue: number;
}

interface ContainerUsage {
  [containerId: string]: number;
}

interface FinalOrderLike extends OrderLike {
  cart: {
    lineItems?: LineItems;
  };
  finalizedLineItems?: {
    weight: {
      adjustCostInCents: number;
    };
    additionalMaterialChargesAdjustedCostInCents: number;
    containers: {
      totalCostInCents: number;
    }[];
  };
}

export const getOrderAdjustments = ({
  order,
  usageByContainer,
  finalWeight,
  additionalMaterials,
  finalContainers,
  allAvailableMaterials,
}: {
  order: FinalOrderLike;
  usageByContainer: ContainerUsage;
  finalWeight: number;
  additionalMaterials: {
    [materialId: string]: number;
  };
  finalContainers: {
    _id: string;
    containerTypeInfo: {
      pricePerDay: number;
    };
  }[];
  allAvailableMaterials: {
    _id: string;
    extraChargeInCents: number;
  }[];
}): OrderAdjustments => {
  const pricePerTon = order.pricePerTonInCents || PRICE_PER_TON_IN_CENT;
  const weightCost = (finalWeight || 0) * pricePerTon;
  const weightCostAdjusted = weightCost - order.cart.lineItems?.materials!;
  const weightBalanceAfterAdjustment =
    weightCostAdjusted || order.finalizedLineItems?.weight.adjustCostInCents || 0;

  const materialCost = Object.keys(additionalMaterials).reduce((acc, materialId) => {
    const material = allAvailableMaterials?.find((m) => m._id === materialId)!;
    return acc + (additionalMaterials[materialId] || 0) * material.extraChargeInCents;
  }, 0);
  const materialCostAdjusted = materialCost;
  const materialsBalanceAfterAdjustment =
    materialCostAdjusted ||
    order.finalizedLineItems?.additionalMaterialChargesAdjustedCostInCents ||
    0;

  const containerCost =
    finalContainers.reduce((acc, c) => {
      return acc + (usageByContainer[c._id] || 0) * (c.containerTypeInfo.pricePerDay / 100);
    }, 0) * 100;

  const containerCostInCents =
    containerCost ||
    (order.finalizedLineItems?.containers ?? []).reduce((acc, c) => {
      return acc + c.totalCostInCents;
    }, 0);
  const containerBalanceAfterAdjustment =
    containerCostInCents -
    order.cart.lineItems!.containers.reduce((acc, c) => {
      return acc + c.totalInCents;
    }, 0);
  return {
    weightAdjustment: weightCostAdjusted,
    containerAdjustment: containerCost,
    materialsAdjustment: materialCostAdjusted,
    weightBalanceAfterAdjustment,
    materialsBalanceAfterAdjustment,
    containerBalanceAfterAdjustment,
    // Legacy function - no refund logic (values derived internally)
    containerRefundDue: 0,
    weightRefundDue: 0,
    balanceDaysDue: 0,
    balanceTonsDue: 0,
  };
};

export interface PricingContext {
  pricingModel: PricingTypeEnum;
  includedDays?: number;
  includedTonnage?: number;
  dailyOverageFeeInCents?: number;
  tonOverageInCents?: number;
  perTonRateInCents?: number;
  dailyRateInCents?: number; // Daily rate for PAY_AS_YOU_GO pricing model
}

export const getPricingAwareOrderAdjustments = ({
  order,
  usageByContainer,
  finalWeight,
  additionalMaterials,
  finalContainers,
  allAvailableMaterials,
  pricingContext,
}: {
  order: FinalOrderLike;
  usageByContainer: ContainerUsage;
  finalWeight: number;
  additionalMaterials: {
    [materialId: string]: number;
  };
  finalContainers: {
    _id: string;
    containerTypeInfo: {
      pricePerDay: number;
    };
  }[];
  allAvailableMaterials: {
    _id: string;
    extraChargeInCents: number;
  }[];
  pricingContext: PricingContext;
}): OrderAdjustments => {
  // Derive prepayment values from Order/Cart model
  const totalDaysPaidAtCheckout = order.cart.lineItems?.containers?.[0]?.totalDays || 0;
  const includedDays = pricingContext.includedDays || 0;
  const extraDaysPaidAtCheckout = Math.max(0, totalDaysPaidAtCheckout - includedDays);

  // Derive admin adjustments from additionalLineItems
  const adminAddedDays =
    order.additionalLineItems
      ?.filter(
        (item) =>
          item.label.toLowerCase().includes('additional days') ||
          item.label.toLowerCase().includes('extra days'),
      )
      .reduce(
        (sum, item) => sum + Math.floor(item.amount / (pricingContext.dailyOverageFeeInCents || 1)),
        0,
      ) || 0;

  const adminAddedTons =
    order.additionalLineItems
      ?.filter(
        (item) =>
          item.label.toLowerCase().includes('additional tons') ||
          item.label.toLowerCase().includes('extra tons'),
      )
      .reduce((sum, item) => {
        const perTonRate =
          pricingContext.pricingModel === PricingTypeEnum.BUNDLE
            ? pricingContext.tonOverageInCents || 0
            : pricingContext.perTonRateInCents || order.pricePerTonInCents || PRICE_PER_TON_IN_CENT;
        return sum + (perTonRate > 0 ? item.amount / perTonRate : 0);
      }, 0) || 0;
  // Calculate material costs (same across all pricing models)
  const materialCost = Object.keys(additionalMaterials).reduce((acc, materialId) => {
    const material = allAvailableMaterials?.find((m) => m._id === materialId)!;
    return acc + (additionalMaterials[materialId] || 0) * material.extraChargeInCents;
  }, 0);
  const materialCostAdjusted = materialCost;
  const materialsBalanceAfterAdjustment =
    materialCostAdjusted ||
    order.finalizedLineItems?.additionalMaterialChargesAdjustedCostInCents ||
    0;

  // Enhanced settlement logic with prepayment/refund calculations
  const totalActualDaysUsed = Object.values(usageByContainer).reduce((sum, days) => sum + days, 0);

  // Settlement helpers (matching the acceptance criteria from specifications)
  const settleDays = ({
    includedDays,
    actualDaysKept,
    extraDaysPaidAtCheckout: extraPaid,
    adminAddedDays: adminDays,
  }: {
    includedDays: number;
    actualDaysKept: number;
    extraDaysPaidAtCheckout: number;
    adminAddedDays: number;
  }) => {
    const daysOver = Math.max(0, actualDaysKept - includedDays);
    const balanceDaysCore = Math.max(0, daysOver - extraPaid);
    const refundDays = Math.max(0, extraPaid - daysOver);
    const balanceDaysDue = balanceDaysCore + Math.max(0, adminDays);
    return { balanceDaysDue, refundDays };
  };

  const settleTons = ({
    includedTons = 0,
    actualTonsUsed = 0,
    adminAddedTons: adminTons = 0,
    isBundle,
  }: {
    includedTons?: number;
    actualTonsUsed?: number;
    adminAddedTons?: number;
    isBundle: boolean;
  }) => {
    if (!isBundle) return { balanceTonsDue: Math.max(0, adminTons) };
    const tonsOver = Math.max(0, actualTonsUsed - includedTons);
    const balanceTonsDue = tonsOver + Math.max(0, adminTons);
    return { balanceTonsDue };
  };

  // Calculate weight and container costs based on pricing model with refund logic
  let weightCostAdjusted = 0;
  let containerCostAdjusted = 0;
  let containerRefundDue = 0;
  const weightRefundDue = 0;
  let balanceDaysDue = 0;
  let balanceTonsDue = 0;

  switch (pricingContext.pricingModel) {
    case PricingTypeEnum.FIXED: {
      // Fixed pricing: charge for all weight, with prepayment/refund logic for containers
      const pricePerTon =
        pricingContext.perTonRateInCents || order.pricePerTonInCents || PRICE_PER_TON_IN_CENT;
      const weightCost = (finalWeight || 0) * pricePerTon;
      weightCostAdjusted = weightCost - (order.cart.lineItems?.materials || 0);

      // Enhanced container settlement with prepayment logic
      const daySettlement = settleDays({
        includedDays: pricingContext.includedDays || 0,
        actualDaysKept: totalActualDaysUsed,
        extraDaysPaidAtCheckout,
        adminAddedDays,
      });

      const tonSettlement = settleTons({
        adminAddedTons,
        isBundle: false,
      });

      balanceDaysDue = daySettlement.balanceDaysDue;
      balanceTonsDue = tonSettlement.balanceTonsDue;

      // Calculate actual costs
      containerCostAdjusted =
        daySettlement.balanceDaysDue * (pricingContext.dailyOverageFeeInCents || 0);
      containerRefundDue = daySettlement.refundDays * (pricingContext.dailyOverageFeeInCents || 0);

      // Add admin tons cost if applicable
      if (tonSettlement.balanceTonsDue > 0) {
        weightCostAdjusted += tonSettlement.balanceTonsDue * pricePerTon;
      }

      break;
    }

    case PricingTypeEnum.BUNDLE: {
      // Bundle pricing: with prepayment/refund logic for both containers and weight
      const daySettlement = settleDays({
        includedDays: pricingContext.includedDays || 0,
        actualDaysKept: totalActualDaysUsed,
        extraDaysPaidAtCheckout,
        adminAddedDays,
      });

      const tonSettlement = settleTons({
        includedTons: pricingContext.includedTonnage || 0,
        actualTonsUsed: finalWeight || 0,
        adminAddedTons,
        isBundle: true,
      });

      balanceDaysDue = daySettlement.balanceDaysDue;
      balanceTonsDue = tonSettlement.balanceTonsDue;

      // Calculate weight cost (only overage + admin additions)
      const weightCost = tonSettlement.balanceTonsDue * (pricingContext.tonOverageInCents || 0);
      weightCostAdjusted = weightCost - (order.cart.lineItems?.materials || 0);

      // Calculate container cost (only overage)
      containerCostAdjusted =
        daySettlement.balanceDaysDue * (pricingContext.dailyOverageFeeInCents || 0);
      containerRefundDue = daySettlement.refundDays * (pricingContext.dailyOverageFeeInCents || 0);

      break;
    }

    case PricingTypeEnum.PAY_AS_YOU_GO:
    default: {
      // Pay-as-you-go: Use quoted rates from pricingContext with fallbacks
      const pricePerTon =
        pricingContext.perTonRateInCents || order.pricePerTonInCents || PRICE_PER_TON_IN_CENT;
      const weightCost = (finalWeight || 0) * pricePerTon;
      weightCostAdjusted = weightCost - (order.cart.lineItems?.materials || 0);

      const containerCost = finalContainers.reduce((acc, c) => {
        // Use quoted daily rate or fall back to static container rate
        const dailyRate =
          pricingContext.dailyRateInCents && pricingContext.dailyRateInCents > 0
            ? pricingContext.dailyRateInCents
            : c.containerTypeInfo.pricePerDay;
        return acc + (usageByContainer[c._id] || 0) * dailyRate;
      }, 0);

      const originalContainerCost =
        order.cart.lineItems?.containers?.reduce((acc, c) => acc + c.totalInCents, 0) || 0;
      containerCostAdjusted = containerCost - originalContainerCost;

      // Pay-as-you-go doesn't use prepayment logic, so no refunds from this function
      balanceDaysDue = 0;
      balanceTonsDue = 0;
      break;
    }
  }

  const result = {
    weightAdjustment: safeMoneyFromCents(weightCostAdjusted).getAmount(),
    containerAdjustment: safeMoneyFromCents(containerCostAdjusted).getAmount(),
    materialsAdjustment: safeMoneyFromCents(materialCostAdjusted).getAmount(),
    weightBalanceAfterAdjustment: safeMoneyFromCents(
      weightCostAdjusted || order.finalizedLineItems?.weight.adjustCostInCents || 0,
    ).getAmount(),
    materialsBalanceAfterAdjustment: safeMoneyFromCents(
      materialsBalanceAfterAdjustment,
    ).getAmount(),
    containerBalanceAfterAdjustment: safeMoneyFromCents(containerCostAdjusted).getAmount(),
    // Enhanced refund/balance tracking
    containerRefundDue: safeMoneyFromCents(containerRefundDue).getAmount(),
    weightRefundDue: safeMoneyFromCents(weightRefundDue).getAmount(),
    balanceDaysDue,
    balanceTonsDue,
  };
  return result;
};
